import React from 'react';
import { ArrowRight, Database, GitBranch, Globe, Layers, Search, ShieldCheck } from 'lucide-react';
import { addonBySlug } from './addonsData';

const pg = addonBySlug['postgres'];

const capabilities = [
  {
    icon: Search,
    title: 'HNSW vector search',
    body: 'pgvector ships enabled: build HNSW indexes over embedding columns and run approximate nearest-neighbor queries in SQL, next to the rest of your data.',
    code: `CREATE INDEX ON docs
  USING hnsw (embedding vector_cosine_ops);

SELECT id, title
FROM docs
ORDER BY embedding <=> $1
LIMIT 10;`,
  },
  {
    icon: Globe,
    title: 'PostGIS geospatial',
    body: 'PostGIS ships enabled: geometry and geography types, spatial indexes, and distance/containment queries.',
    code: `SELECT name
FROM places
WHERE ST_DWithin(
  geog,
  ST_MakePoint($lng, $lat)::geography,
  5000  -- within 5 km
)
ORDER BY geog <-> ST_MakePoint($lng, $lat)::geography;`,
  },
  {
    icon: GitBranch,
    title: 'Graph traversal',
    body: 'Model edges as rows and traverse with recursive CTEs — multi-hop reachability, shortest paths, and dependency walks without a separate graph database. openCypher via Apache AGE is on the roadmap.',
    code: `WITH RECURSIVE reach AS (
  SELECT dst, 1 AS depth FROM edges WHERE src = $start
  UNION
  SELECT e.dst, r.depth + 1
  FROM edges e JOIN reach r ON e.src = r.dst
  WHERE r.depth < 4
)
SELECT DISTINCT dst, min(depth) FROM reach GROUP BY dst;`,
  },
  {
    icon: Layers,
    title: 'Hybrid retrieval',
    body: 'Combine vector similarity, full-text rank, and recency in one query — or fan out to a gobed addon for GPU-scale candidate generation and rerank in SQL.',
    code: `SELECT id, title,
  0.6 * (1 - (embedding <=> $emb))
+ 0.4 * ts_rank(fts, plainto_tsquery($q)) AS score
FROM docs
WHERE fts @@ plainto_tsquery($q)
   OR embedding <=> $emb < 0.35
ORDER BY score DESC LIMIT 20;`,
  },
];

export const PostgresAddonPage: React.FC = () => (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%)]" />
      <div className="relative mx-auto max-w-5xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/25 bg-sky-400/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-sky-300">
          <Database size={12} /> Hosted addon · featured
        </div>
        <h1 className="mt-6 max-w-[18ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
          Postgres with vectors, maps, and graphs
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-white/72">
          Managed PostgreSQL with pgvector and PostGIS enabled by default, graph traversal through recursive
          CTEs, and hybrid retrieval mixing vector, full-text, and geo in one SQL statement. Attaches to your
          app and agents as <code className="font-mono text-sm">DATABASE_URL</code> in one command.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="/signup" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
            Attach Postgres <ArrowRight size={16} />
          </a>
          <a href="/addons" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
            All addons
          </a>
        </div>
        <pre className="mt-8 max-w-xl overflow-x-auto rounded-xl border border-white/10 bg-black/50 p-4 font-mono text-[13px] leading-6 text-white/80">{`$ app addons create --type postgres --plan starter --attach-default
Provisioning postgres (starter)…
Extensions: vector, postgis
Injected: DATABASE_URL, APP_ADDON_POSTGRES_URL
Ready in 9s`}</pre>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-[1400px]">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">one database, four retrieval engines</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Everything is a SQL query</h2>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {capabilities.map((c) => (
            <div key={c.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <div className="flex items-center gap-3">
                <c.icon size={20} className="text-sky-400" />
                <h3 className="font-display text-lg font-bold">{c.title}</h3>
              </div>
              <p className="mt-3 text-sm leading-7 text-white/65">{c.body}</p>
              <pre className="mt-4 overflow-x-auto rounded-lg bg-black/50 p-4 font-mono text-[12.5px] leading-6 text-white/78">{c.code}</pre>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-sky-400/90">operations</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Backups, rotation, and teardown</h2>
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">Injected environment</p>
            <div className="mt-4 space-y-3">
              {pg.envVars.map((v) => (
                <div key={v.name}>
                  <code className="font-mono text-[13px] font-semibold text-sky-300">{v.name}</code>
                  <p className="text-sm leading-6 text-white/60">{v.description}{v.secret ? ' · secret' : ''}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-3">
              {(pg.plans || []).map((p) => (
                <div key={p.plan} className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
                  <p className="font-display text-lg font-bold">{p.plan}</p>
                  <p className="mt-1 text-sm font-semibold text-sky-400">{p.credits}</p>
                  <p className="mt-2 text-sm leading-6 text-white/60">{p.detail}</p>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-sky-400" />
              <p className="text-sm leading-7 text-white/65">
                Backups billed per GB in credits, credential rotation on demand, secrets scrubbed from
                build and CI logs, one-click test, disable, or delete from the dashboard or CLI, and the same{' '}
                <code className="font-mono">DATABASE_URL</code> for coding agents — so migrations, seeds, and
                query verification are agent tasks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="px-4 py-14 sm:px-8 sm:py-20 lg:px-12">
      <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center sm:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.14),transparent_60%)]" />
        <div className="relative">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-4xl">Start with HNSW in Postgres, add GPUs later</h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-8 text-white/70">
            Start with pgvector HNSW in your database. When a corpus needs GPU throughput, pair it with the
            gobed addon; Postgres stays the source of truth.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="/signup" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
              Get started
            </a>
            <a href="/addons/gobed" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
              gobed GPU search <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  </main>
);

export default PostgresAddonPage;
